import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import useInventoryData from "../utils/useInventoryData";
import { useAuth } from '../AuthContext';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import LabelPrinter from './LabelPrinter';
import MoveItemsModal from "./modals/MoveItemsModal";
import useInventoryModal from "../utils/useInventoryModal";
import InventoryItemModal from "./modals/InventoryItemModal";
import useInventoryEdits from "../utils/useInventoryEdits";
import useInventoryGrid from "../utils/useInventoryGrid";
import RestoreItemsModal from "./modals/RestoreItemModal";
import SectionSelector from "./SectionSelector"; 
import GridToolBar from "./GridToolBar";
import InventoryActionsBar from "./InventoryActionsBar";
import FloatingToast from "./FloatingToast";
import ManualTagsModal from "./modals/ManualTagsModal";
import InventoryHelpModal from "./modals/InventoryHelpModal";
import { 
  titleCase, 
  formatSaleDate, 
  formatSaleDateNoYear,
  toNumberOrNull,
  toIntOr,
  formatUSD 
} from '../utils/formatters';
ModuleRegistry.registerModules([AllCommunityModule]);

function InventoryBottomPager({
  currentPage,
  totalPages,
  onFirst,
  onPrev,
  onNext,
  onLast,
}) {
  const isFirstPage = currentPage <= 0;
  const isLastPage = totalPages === 0 || currentPage >= totalPages - 1;
  const displayCurrent = totalPages === 0 ? 0 : currentPage + 1;
  const displayTotal = totalPages === 0 ? 0 : totalPages;

  return (
    <div className="inventory-bottom-pager">
      <div className="inventory-bottom-pager-inner">
        <span className="inventory-bottom-pager-group">
          <button type="button" onClick={onFirst} disabled={isFirstPage} aria-label="First Page">
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path
                d="M11 4 L7 8 L11 12 M7 4 L3 8 L7 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button type="button" onClick={onPrev} disabled={isFirstPage} aria-label="Previous Page">
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path
                d="M10 4 L6 8 L10 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span className="inventory-bottom-pager-label">{displayCurrent} / {displayTotal}</span>
          <button type="button" onClick={onNext} disabled={isLastPage} aria-label="Next Page">
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path
                d="M6 4 L10 8 L6 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button type="button" onClick={onLast} disabled={isLastPage} aria-label="Last Page">
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path
                d="M5 4 L9 8 L5 12 M9 4 L13 8 L9 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </span>
      </div>
    </div>
  );
}

function SalesInv() {
  const { user, fetchWrapper } = useAuth();
  const clientId = user?.id || 'Not authenticated';
  const [sales, setSales] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState('');
  const [sections, setSections] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState('all');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showManualTagsModal, setShowManualTagsModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [manualTagsLoading, setManualTagsLoading] = useState(false);
  // inventory, loading, status, reload now come from useInventoryData
 const { 
  inventory, 
  reload,
  loading: dataLoading, // Rename if needed
  status: dataStatus // Rename to avoid duplicate
} = useInventoryData({
  saleId: selectedSaleId,
  sectionId: selectedSectionId,
  clientId
});
  const [tagPreset, setTagPreset] = useState('');
  const [uiMessage, setUiMessage] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveSections, setMoveSections] = useState([]);          // merged assigned + globals
  const [moveSelectedSectionId, setMoveSelectedSectionId] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [isQuickEdit, setIsQuickEdit] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [changeHistoryItem, setChangeHistoryItem] = useState(null);
  const [changeHistoryRows, setChangeHistoryRows] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [modalPrice, setModalPrice] = useState('');
  const [modalQty, setModalQty] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [labelsToPrint, setLabelsToPrint] = useState(0); // New state for labels
  const [startAtSheetOpen, setStartAtSheetOpen] = useState(false);
  const [startAtValue, setStartAtValue] = useState(1);
  const [startAtError, setStartAtError] = useState('');
  const [pendingPrint, setPendingPrint] = useState(null);
  const [pendingPartialSheet, setPendingPartialSheet] = useState(false);
  const [pendingStartAtValue, setPendingStartAtValue] = useState(null);
  const [lastOrderMode, setLastOrderMode] = useState('walk');
  const [moveError, setMoveError] = useState('');  // ← ADD THIS
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 0,
    totalPages: 0,
  });
  const manualTagsSectionName = 'Manual Tags';

const {
  commitTitle,
  commitPrice,
  commitQty,
  commitAll
} = useInventoryEdits({
  clientId,
  saleId: selectedSaleId,
  fetchWrapper
});

const handleOpenChangeHistory = useCallback((item) => {
  const edits = Array.isArray(item?.recentEdits) ? item.recentEdits : [];
  if (!edits.length) return;
  const rows = [...edits]
    .sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at))
    .map((edit, index) => ({
      id: edit.id ?? `${item.id}-${index}`,
      changed_at: edit.changed_at,
      field_name: edit.field_name,
      old_value: edit.old_value ?? '',
      new_value: edit.new_value ?? '',
    }));
  setChangeHistoryItem(item);
  setChangeHistoryRows(rows);
  setShowChangeHistory(true);
}, []);

  const sortPersistenceKey = useMemo(() => {
    if (!selectedSaleId) return '';
    return `salesinv:${selectedSaleId}:${selectedSectionId}`;
  }, [selectedSaleId, selectedSectionId]);

  const {
    gridRef,
    pageRows,
    selectedCount,
    sortModel,
    hasColumnChanges,
    onGridReady,
    onFirstDataRendered,
    handleSearchChange,
    handleSortChanged,
    handleSelectionChanged,    
    handleRowClicked,
    clearSortModel,
    resetColumns,
    handleColumnStateChange,
    context,      // ← NEW
    columnDefs    // ← ADD: from useInventoryGrid
  } = useInventoryGrid({
    inventory,
    isQuickEdit,
    sortPersistenceKey,
    categoryOptions,
    onThumbnailClick: (item) => {
      // your modal-opening logic
      setSelectedItem(item);
      setSelectedImage(item.url);

      setModalTitle(String(item.title || ''));
      setModalPrice(
        toNumberOrNull(item.per_item_cost_estimate) ?? ''
      );
      setModalQty(
        String(toIntOr(item.quantity_estimate, 1))
      );

      setShowModal(true);
    },
    onViewChangeHistory: handleOpenChangeHistory,
  });

  const refreshPaginationInfo = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api || typeof api.paginationGetCurrentPage !== 'function') return;
    const next = {
      currentPage: api.paginationGetCurrentPage(),
      totalPages: api.paginationGetTotalPages(),
    };
    setPaginationInfo((prev) => {
      if (
        prev.currentPage === next.currentPage &&
        prev.totalPages === next.totalPages
      ) {
        return prev;
      }
      return next;
    });
  }, [gridRef]);

  const handleGridReadyWithPager = useCallback((params) => {
    onGridReady(params);
    setTimeout(refreshPaginationInfo, 0);
  }, [onGridReady, refreshPaginationInfo]);

  const handleFirstDataRenderedWithPager = useCallback((params) => {
    onFirstDataRendered(params);
    setTimeout(refreshPaginationInfo, 0);
  }, [onFirstDataRendered, refreshPaginationInfo]);

  const handleSortChangedWithPager = useCallback((event) => {
    handleSortChanged(event);
    refreshPaginationInfo();
  }, [handleSortChanged, refreshPaginationInfo]);
const [clientInfo, setClientInfo] = useState(null);

const fetchClientInfo = useCallback(async () => {
  if (!clientId) return;
  try {
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_client`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ p_client_id: clientId }),
    });
    if (!response.ok) throw new Error('Failed to fetch client');
    const json = await response.json();
    const row = Array.isArray(json) ? json[0] : json;
    setClientInfo(row || null);
  } catch (error) {
    console.error('Fetch client info error:', error);
    setClientInfo(null);
  }
}, [clientId, fetchWrapper]);

const fetchCategoryOptions = useCallback(async () => {
  try {
    const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_inventory_categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch categories');
    }
    const rows = await response.json();
    const values = (Array.isArray(rows) ? rows : [])
      .map((row) => String(row?.category || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    setCategoryOptions(values);
  } catch (error) {
    console.error('Fetch category options error:', error);
    setCategoryOptions([]);
  }
}, [fetchWrapper]);

useEffect(() => {
  if (user) {
    fetchClientInfo();
    fetchCategoryOptions();
  }
}, [user, fetchCategoryOptions, fetchClientInfo]);
const businessName = useMemo(() => {
  return String(
    (clientInfo && (
      clientInfo.business_name ??
      clientInfo.company_name ??
      clientInfo.display_name ??
      clientInfo.name
    )) ?? (user?.user_metadata?.business_name) ?? ''
  ).trim();
}, [clientInfo, user]);

const getColumnHeaderName = useCallback(
  (field) => {
    const fallback = String(field || '').replace(/_/g, ' ').trim() || 'Field';

    // Prefer the grid's resolved colDef (works even if columnApi isn't exposed on the ref).
    const api = gridRef.current?.api;
    const apiColDef =
      api && typeof api.getColumnDef === 'function' ? api.getColumnDef(field) : null;
    if (apiColDef?.headerName) return apiColDef.headerName;

    // Fallback: search the current columnDefs (quick or normal) supplied by the hook.
    const fromDefs = (columnDefs || []).find(
      (d) => d?.field === field || d?.colId === field
    );
    if (fromDefs?.headerName) return fromDefs.headerName;

    return fallback;
  },
  [gridRef, columnDefs]
);

const formatChangeDate = useCallback((value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}, []);

const changeHistoryColumnDefs = useMemo(
  () => [
    {
      field: 'changed_at',
      headerName: 'Date',
      minWidth: 160,
      flex: 0.9,
      valueFormatter: (p) => formatChangeDate(p.value),
    },
    {
      field: 'field_name',
      headerName: 'Field',
      minWidth: 140,
      flex: 0.8,
      valueGetter: (p) => getColumnHeaderName(p.data?.field_name),
    },
    {
      field: 'old_value',
      headerName: 'Old Data',
      minWidth: 180,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      cellStyle: { whiteSpace: 'normal' },
    },
    {
      field: 'new_value',
      headerName: 'New Data',
      minWidth: 180,
      flex: 1,
      wrapText: true,
      autoHeight: true,
      cellStyle: { whiteSpace: 'normal' },
    },
  ],
  [formatChangeDate, getColumnHeaderName]
);

const closeChangeHistory = useCallback(() => {
  setShowChangeHistory(false);
  setChangeHistoryItem(null);
  setChangeHistoryRows([]);
}, []);
  const [printOrderPrompt, setPrintOrderPrompt] = useState({
    isOpen: false,
    sortLabel: '',
  });
  const currentSale = useMemo(() => sales.find((s) => s.id === selectedSaleId), [sales, selectedSaleId]);
  const currentSaleName = currentSale?.sale_name || 'Sale';

  const selectedSectionName = useMemo(() => {
    if (selectedSectionId === 'all') return 'All Sections';
    return sections.find((section) => section.id === selectedSectionId)?.name || 'Selected Section';
  }, [sections, selectedSectionId]);

  const displayedAskingTotal = useMemo(() => {
    return (inventory || []).reduce((sum, item) => {
      const unitPrice = toNumberOrNull(item?.per_item_cost_estimate);
      if (unitPrice === null) return sum;
      const quantity = toIntOr(item?.quantity_estimate, 1);
      return sum + unitPrice * quantity;
    }, 0);
  }, [inventory]);

  const displayedAskingTotalLabel = useMemo(() => {
    const n = Number(displayedAskingTotal);
    if (!Number.isFinite(n)) return '$0.00';
    return n.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [displayedAskingTotal]);

  const getSectionName = useCallback(
    async (sectionId) => {
      if (sectionId === 'all') return currentSaleName;
      try {
        const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/select_sale_section_by_id`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_section_id: sectionId, p_sale_id: selectedSaleId }),
        });
        const data = await response.json();
        return Array.isArray(data) && data.length > 0 ? data[0].name : currentSaleName;
      } catch (error) {
        console.error('Error fetching section name:', error);
        return currentSaleName;
      }
    },
    [selectedSaleId, currentSaleName, fetchWrapper]
  );

  const fetchSales = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetchWrapper(
        `${process.env.REACT_APP_API_URL}/supabase/rpc/select_all_sales`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ p_client_id: clientId }),
        }
      );
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        const activeSales = data.filter(sale => sale.status === 'active');
        setSales(activeSales);
      } else {
        console.error('Failed to fetch sales:', data);
      setUiMessage('Error fetching sales. Contact support@scucciai.com');
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
      setUiMessage(`Error fetching sales: ${error.message}. Contact support@scucciai.com`);
    }
  }, [clientId, fetchWrapper, user]);

  const fetchSections = useCallback(
    async (saleId) => {
      if (!saleId || !user) {
        setSections([]);
        return;
      }
      try {
        const response = await fetchWrapper(
          `${process.env.REACT_APP_API_URL}/supabase/rpc/select_sale_sections_by_sale_id`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ p_sale_id: saleId }),
          }
        );
        if (!response) throw new Error('Sections fetch failed');
        const sectionsData = await response.json();
        const sortedSections = sectionsData.sort((a, b) => a.name.localeCompare(b.name));
        setSections(sortedSections);
        if (sortedSections.length === 0) {
          setUiMessage('⚠️ No sections for this sale. Upload images to create.');
        } else {
         setUiMessage('');
        }
      } catch (error) {
        console.error('Sections fetch error:', error);
        setUiMessage(`❌ Failed to load sections: ${error.message}`);
        setSections([]);
      }
    },
    [clientId, fetchWrapper, user]
  );

  const handleOpenManualTags = () => {
    if (!selectedSaleId) {
      setUiMessage('Please select a sale first.');
      return;
    }
    setShowManualTagsModal(true);
  };

  const resolveManualTagsSection = useCallback(async () => {
    const existing = sections.find(
      (section) =>
        (section.name || '').trim().toLowerCase() === manualTagsSectionName.toLowerCase()
    );
    if (existing) {
      return { sectionId: existing.id, sectionName: existing.name, sectionsForPrint: sections };
    }

    const response = await fetchWrapper(
      `${process.env.REACT_APP_API_URL}/supabase/rpc/insert_sale_section`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          p_sale_id: selectedSaleId,
          p_name: manualTagsSectionName,
        }),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to create manual tags section');
    }
    const newSectionId = await response.json();
    await fetchSections(selectedSaleId);

    const sectionsForPrint = [
      ...sections,
      { id: newSectionId, name: manualTagsSectionName },
    ];
    return { sectionId: newSectionId, sectionName: manualTagsSectionName, sectionsForPrint };
  }, [fetchSections, fetchWrapper, manualTagsSectionName, sections, selectedSaleId]);

  const handleManualTagsSubmit = async (rows) => {
    if (!tagPreset) {
      setUiMessage('Please select a label type');
      return;
    }

    const itemsToCreate = (rows || [])
      .filter((row) => row.selected)
      .map((row) => ({
        title: String(row.title || '').trim(),
        quantity: row.quantity === '' ? '' : Number(row.quantity || 1),
        price: row.price === '' ? '' : String(row.price),
      }))
      .filter((row) => row.title);

    if (itemsToCreate.length === 0) {
      setUiMessage('Select at least one titled row to print.');
      return;
    }

    setManualTagsLoading(true);
    try {
      const { sectionId, sectionName, sectionsForPrint } = await resolveManualTagsSection();

      const uploadLogResponse = await fetchWrapper(
        `${process.env.REACT_APP_API_URL}/supabase/rpc/get_or_create_manual_upload_log`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_client_id: clientId,
            p_sale_id: selectedSaleId,
            p_section_id: sectionId,
          }),
        }
      );
      if (!uploadLogResponse.ok) {
        const errorData = await uploadLogResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to prepare manual upload log');
      }
      const uploadLogId = await uploadLogResponse.json();

      const insertResponse = await fetchWrapper(
        `${process.env.REACT_APP_API_URL}/supabase/rpc/insert_manual_inventory_items`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            p_upload_log_id: uploadLogId,
            p_client_id: clientId,
            p_sale_id: selectedSaleId,
            p_section_id: sectionId,
            p_items: itemsToCreate,
          }),
        }
      );
      if (!insertResponse.ok) {
        const errorData = await insertResponse.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create manual items');
      }
      const insertedItems = await insertResponse.json();
      if (!Array.isArray(insertedItems) || insertedItems.length === 0) {
        setUiMessage('No manual tags were created.');
        return;
      }

      const { fileName, successMessage } = await LabelPrinter.generate({
        items: insertedItems,
        presetKey: tagPreset,
        businessName,
        saleName: currentSaleName,
        sectionId,
        onStatus: setUiMessage,
        currentSale,
        formatSaleDateNoYear,
        getSectionName: async () => sectionName,
        selectedSectionId: sectionId,
        sections: sectionsForPrint,
        selectedSaleId,
        fetchWrapper,
        clientId,
        enableSpacers: true,
        fetchInventory: reload,
        setStatus: setUiMessage,
        toIntOr,
        slug: (s) =>
          String(s || '')
            .normalize('NFKD')
            .replace(/[^\w\s-]/g, '')
            .trim()
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .toLowerCase(),
        firstTwoWords: (s) => String(s || '').trim().split(/\s+/).slice(0, 2).join(' '),
      });

      setUiMessage(successMessage || `Downloaded ${fileName}. Check your Downloads folder.`);
      setLabelsToPrint(0);
      setShowManualTagsModal(false);
      await reload();
    } catch (error) {
      setUiMessage(`Manual tags failed: ${error.message}`);
    } finally {
      setManualTagsLoading(false);
      setTimeout(() => setUiMessage(''), 6000);
    }
  };

    const fetchMoveSections = useCallback(
    async (saleId) => {
      if (!saleId || !user) {
        setMoveSections([]);
        return;
      }
      try {
        setMoveLoading(true);
        setMoveError('');

        // 1) Sections already assigned to this sale
        const assignedRes = await fetchWrapper(
          `${process.env.REACT_APP_API_URL}/supabase/rpc/select_sale_sections_by_sale_id`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ p_sale_id: saleId }),
          }
        );
        if (!assignedRes) throw new Error('Assigned fetch failed');
        const assignedData = await assignedRes.json();

        // 2) Global section descriptors
        const globalsRes = await fetchWrapper(
          `${process.env.REACT_APP_API_URL}/supabase/rpc/get_section_descs`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          }
        );
        if (!globalsRes) throw new Error('Globals fetch failed');
        const globalsData = await globalsRes.json();

        // 3) Merge: assigned first, then globals not already assigned
        const assignedNames = new Set(assignedData.map((s) => s.name));
        const availableGlobals = globalsData.filter(
          (g) => !assignedNames.has(g.name)
        );

        const merged = [
          ...assignedData.map((s) => ({ ...s, is_assigned: true })),
          ...availableGlobals.map((g) => ({ ...g, is_assigned: false })),
        ];

        merged.sort((a, b) => {
          if (a.is_assigned !== b.is_assigned) {
            return a.is_assigned ? -1 : 1; // assigned sections first
          }
          return a.name.localeCompare(b.name);
        });

        setMoveSections(merged);
        setMoveSelectedSectionId('');
        setMoveError(
          merged.length === 0
            ? 'No sections available. Define a section first in Uploader.'
            : ''
        );
      } catch (err) {
        console.error('Move sections fetch error:', err);
        setMoveError(`Failed to load sections for move: ${err.message}`);
        setMoveSections([]);
      } finally {
        setMoveLoading(false);
      }
    },
    [user, fetchWrapper]
  );

   useEffect(() => {
  if (gridRef.current?.api) {
    const selected = gridRef.current.api.getSelectedRows() || [];
    setLabelsToPrint(selected.reduce((sum, item) => sum + toIntOr(item?.quantity_estimate, 1), 0));
  }
}, [selectedCount, gridRef]);
  useEffect(() => {
    if (user) {
      fetchSales();
    }
  }, [user, fetchSales]);

  useEffect(() => {
    if (selectedSaleId) {
      fetchSections(selectedSaleId);
    } else {
      setSections([]);
      setSelectedSectionId('all');
    }
    setTagPreset('');
  }, [selectedSaleId, fetchSections]);
  useEffect(() => {
    setTagPreset('');
  }, [selectedSectionId]);
 
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
        setSelectedImage('');
      }
    };
    if (showModal) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showModal]);
  useEffect(() => {
  if (
    uiMessage &&
    !uiMessage.toLowerCase().includes('error') &&
    !uiMessage.toLowerCase().includes('failed')
  ) {
    const timer = setTimeout(() => setUiMessage(''), 5000);
    return () => clearTimeout(timer);
  }
}, [uiMessage]);
  useEffect(() => {
    document.documentElement.classList.add('route-salesinv');
    return () => {
      document.documentElement.classList.remove('route-salesinv');
    };
  }, []);   



  const getFilteredRows = () => {
    const api = gridRef.current?.api;
    if (!api) return inventory || [];
    const rows = [];
    api.forEachNodeAfterFilter((node) => {
      if (node?.data) rows.push(node.data);
    });
    return rows;
  };

  const getTableRows = () => {
    const api = gridRef.current?.api;
    if (!api) return inventory || [];
    const rows = [];
    api.forEachNodeAfterFilterAndSort((node) => {
      if (node?.data) rows.push(node.data);
    });
    return rows;
  };

  const getSelectedIds = () => {
    const api = gridRef.current?.api;
    const selected = api?.getSelectedRows?.() || [];
    if (!selected.length) return null;
    return new Set(selected.map((row) => row.id));
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  };

  const toSortableNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : Number.POSITIVE_INFINITY;
  };

  const compareWalkOrder = (a, b) => {
    const aUploaded = parseDateValue(a?.uploaded_at);
    const bUploaded = parseDateValue(b?.uploaded_at);
    if (aUploaded !== null || bUploaded !== null) {
      if (aUploaded === null) return 1;
      if (bUploaded === null) return -1;
      if (aUploaded !== bUploaded) return aUploaded - bUploaded;
    }

    const aLog = toSortableNumber(a?.upload_log_id);
    const bLog = toSortableNumber(b?.upload_log_id);
    if (aLog !== bLog) return aLog - bLog;

    const aIndex = toSortableNumber(a?.item_index);
    const bIndex = toSortableNumber(b?.item_index);
    if (aIndex !== bIndex) return aIndex - bIndex;

    const aId = String(a?.id ?? '');
    const bId = String(b?.id ?? '');
    return aId.localeCompare(bId);
  };

  const sortWalkOrder = (items) =>
    (items || []).slice().sort(compareWalkOrder);

  const buildPrintItems = (orderMode) => {
    const baseRows = orderMode === 'table' ? getTableRows() : getFilteredRows();
    const selectedIds = getSelectedIds();
    const scopedRows = selectedIds
      ? baseRows.filter((row) => selectedIds.has(row.id))
      : baseRows;

    if (orderMode === 'walk') {
      return sortWalkOrder(scopedRows);
    }

    return scopedRows;
  };
 
  
    const handleOpenMoveModal = useCallback(async () => {
    if (!selectedSaleId) {
    setUiMessage('Please select a sale before moving items.');
      return;
    }
    const selectedRows = gridRef.current?.api?.getSelectedRows() || [];
    if (!selectedRows.length) {
      setUiMessage('Select one or more items to move.');
      return;
    }

    try {
      await fetchMoveSections(selectedSaleId);
      setShowMoveModal(true);
    } catch {
    }
  }, [selectedSaleId, fetchMoveSections, setUiMessage]);
  const handleUndoLastEdit = async () => {
    if (!selectedSaleId) {
      setUiMessage('Please select a sale first');
      return;
    }

  const confirmed = window.confirm('Undo the most recent edit in this sale?');
  if (!confirmed) return;

  setUiMessage('Finding last edit...');

  try {
 const resp = await fetchWrapper(
  `${process.env.REACT_APP_API_URL}/supabase/rpc/get_latest_inventory_changes`,
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      p_sale_id: selectedSaleId,
      p_item_ids: [],  // ← empty array is fine — we want latest across all items
    }),
  }
);

    if (!resp.ok) throw new Error('Failed to fetch last edit');

    const changes = await resp.json();

    if (changes.length === 0) {
      setUiMessage('No recent edits to undo');
      return;
    }

      const change = changes[0];
      const itemId = change.inventory_item_id;
      const field = change.field_name;
      const oldValue = change.old_value;
      const fieldLabel = getColumnHeaderName(field);

    switch (field) {
  case 'title':
    await commitTitle(itemId, oldValue);
    break;
  case 'per_item_cost_estimate':
    await commitPrice(itemId, oldValue);
    break;
  case 'quantity_estimate':
    await commitQty(itemId, oldValue);
    break;
  case 'category':
    // Existing RPC: update_inventory_item_category
    await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/update_inventory_item_category`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        p_id: itemId,
        p_sale_id: selectedSaleId,
        p_client_id: clientId,
        p_category: oldValue || null
      })
    });
    break;
  case 'description':
    // Existing RPC: update_inventory_item_description
    await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/update_inventory_item_description`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        p_id: itemId,
        p_sale_id: selectedSaleId,
        p_client_id: clientId,
        p_description: oldValue || null
      })
    });
    break;
  case 'resale_positioning':
    // Existing RPC: update_inventory_item_resale_positioning
    const resaleArray = oldValue ? oldValue.split(', ').filter(Boolean) : [];
    await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/update_inventory_item_resale_positioning`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        p_id: itemId,
        p_sale_id: selectedSaleId,
        p_client_id: clientId,
        p_resale_positioning: resaleArray
      })
    });
    break;
  case 'pricing_context':
    // Existing RPC: update_inventory_item_pricing_context
    const pricingArray = oldValue ? oldValue.split(', ').filter(Boolean) : [];
    await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/update_inventory_item_pricing_context`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        p_id: itemId,
        p_sale_id: selectedSaleId,
        p_client_id: clientId,
        p_pricing_context: pricingArray
      })
    });
    break;
    default:
      setUiMessage(`Cannot undo "${fieldLabel}" edits yet`);
      return;
  }

      await reload();
      setUiMessage(`Undid last edit on "${fieldLabel}"`);
    } catch (err) {
      setUiMessage(`Undo failed: ${err.message}`);
    }
  };
  const handleCloseMoveModal = useCallback(() => {
    setShowMoveModal(false);
    setMoveSelectedSectionId('');
    setMoveError('');
  }, []);


  const getActiveSortInfo = () => {
    const active = (sortModel || []).filter(
      (col) => col.sort === 'asc' || col.sort === 'desc'
    );
    if (!active.length) return null;
    if (active.length > 1) return { label: 'multiple columns' };
    const label = getColumnHeaderName(active[0]?.colId);
    return { label: label || 'this column' };
  };

  const isSheetPreset = (presetKey) =>
    ['AVERY_5160', 'AVERY_5160_QR', 'AVERY_5167', 'AVERY_94102', 'PLS275'].includes(presetKey);

  const getSheetMax = (presetKey) => {
    switch (presetKey) {
      case 'AVERY_5160_QR':
      case 'AVERY_5160':
        return 30;
      case 'AVERY_5167':
        return 80;
      case 'AVERY_94102':
        return 80;
      case 'PLS275':
        return 160;
      default:
        return 0;
    }
  };

  const openStartAtSheet = (orderMode) => {
    const items = buildPrintItems(orderMode);
    if (items.length === 0) {
      setUiMessage('No items to process. Select some rows or load an inventory.');
      return;
    }
    setPendingPrint({ orderMode, items });
    setStartAtValue(1);
    setStartAtError('');
    setStartAtSheetOpen(true);
  };

  const doPrintLabelsWithItems = async (orderMode, items, startAt) => {
    try {
      const { fileName, successMessage, fileUrl } = await LabelPrinter.generate({ // FIXED: Assume generate returns fileUrl (or blob URL)
        items,
        presetKey: tagPreset,
        businessName,
        saleName: currentSaleName,
        sectionId: selectedSectionId === 'all' ? 'all' : selectedSectionId,
        onStatus: setUiMessage,
        currentSale,
        formatSaleDateNoYear,
        getSectionName,
        selectedSectionId,
        sections,
        selectedSaleId,
        fetchWrapper,
        clientId,
        enableSpacers: orderMode === 'walk',
        startAt,
        reload,
        setUiMessage,
        toIntOr,
        slug: (s) => String(s || '').normalize('NFKD').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').toLowerCase(),
        firstTwoWords: (s) => String(s || '').trim().split(/\s+/).slice(0, 2).join(' '),
      });
      setUiMessage(successMessage || `Generated ${fileName}`);
      setLabelsToPrint(0);
      setTagPreset('');
      gridRef.current?.api?.deselectAll?.();
    } catch (err) {  // <- This catch should follow directly (no setTagPreset in between)
      console.error('Label generation error:', err);
      setUiMessage(`Error: ${err.message}`);
    } finally {
     // Removed local setLoading
      setTimeout(() => setUiMessage(''), 4000);
    }
  };

  const doPrintLabels = async (orderMode) => {
    if (!tagPreset) {
      setUiMessage('Please select a label type');
      return;
    }
    const items = buildPrintItems(orderMode);
    if (items.length === 0) {
      setUiMessage('No items to process. Select some rows or load an inventory.');
      return;
    }
    setLastOrderMode(orderMode);
    return doPrintLabelsWithItems(orderMode, items, 1);
  };

  const handleMakeLabels = () => {
    setPendingPartialSheet(false);
    setPendingStartAtValue(null);
    const activeSort = getActiveSortInfo();
    if (activeSort) {
      setPrintOrderPrompt({
        isOpen: true,
        sortLabel: activeSort.label,
      });
      return;
    }
    doPrintLabels('walk');
  };

  const handlePrintOrderChoice = (orderMode) => {
    setPrintOrderPrompt({
      isOpen: false,
      sortLabel: '',
    });
    setLastOrderMode(orderMode);
    if (pendingPartialSheet) {
      const startAt = pendingStartAtValue;
      setPendingPartialSheet(false);
      setPendingStartAtValue(null);
      if (startAt != null) {
        const items = buildPrintItems(orderMode);
        if (items.length === 0) {
          setUiMessage('No items to process. Select some rows or load an inventory.');
          return;
        }
        doPrintLabelsWithItems(orderMode, items, startAt);
        return;
      }
    }
    doPrintLabels(orderMode);
  };

  const handleOpenPartialSheet = () => {
    const orderMode = lastOrderMode || 'walk';
    openStartAtSheet(orderMode);
  };

  const handleStartAtCancel = () => {
    setStartAtSheetOpen(false);
    setStartAtError('');
    setPendingPrint(null);
  };

  const handleStartAtGenerate = () => {
    const max = getSheetMax(tagPreset);
    const raw = typeof startAtValue === 'string' ? parseInt(startAtValue, 10) : startAtValue;
    const next = Number.isFinite(raw) ? raw : 0;
    if (next < 1 || (max && next > max)) {
      setStartAtError(`Enter a number from 1 to ${max}.`);
      return;
    }
    if (!pendingPrint?.items || !pendingPrint?.orderMode) {
      setStartAtError('Missing print data. Try again.');
      return;
    }
    setStartAtError('');
    setStartAtSheetOpen(false);
    const activeSort = getActiveSortInfo();
    if (activeSort) {
      setPendingPartialSheet(true);
      setPendingStartAtValue(next);
      setPendingPrint(null);
      setPrintOrderPrompt({
        isOpen: true,
        sortLabel: activeSort.label,
      });
      return;
    }
    const { orderMode, items } = pendingPrint;
    setPendingPrint(null);
    doPrintLabelsWithItems(orderMode, items, next);
  };

  const handleExportCsv = () => {
    if (gridRef.current && gridRef.current.api) {
      const sectionName =
        selectedSectionId === 'all'
          ? 'allsections'
          : sections.find((s) => s.id === selectedSectionId)?.name || 'allsections';
      const sanitizedSection = sectionName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      gridRef.current.api.exportDataAsCsv({
        fileName: `inventory_${sanitizedSection}.csv`,
        prependContent:
          'ScucciAI Estate Sales Inventory\nInventory Details\nGenerated on ' + new Date().toLocaleDateString() + '\n',
        columnKeys: [
        'filename',
        'section_name',
        'title',
        'description',
        'resale_positioning',
        'pricing_context',
        'quantity_estimate',       // ✅ NEW: Qty column in CSV
        'per_item_cost_estimate',
      ],
      });
    } else {
    setUiMessage('Error: Grid not ready for export.');
    }
  };

  const toggleQuickEdit = () => {
    const newIsQuickEdit = !isQuickEdit;
    setIsQuickEdit(newIsQuickEdit);

    if (gridRef.current?.api) {
      // Only size-to-fit when returning to the full grid
      if (!newIsQuickEdit) {
        setTimeout(() => {
          gridRef.current.api.sizeColumnsToFit({ skipPinned: true });
        }, 50);
      }
    }

    setUiMessage(newIsQuickEdit ? 'Switched to Focus Mode. Edit title, quantity, and price.' : 'Restored full grid view.');
  };
  
  const handleCellUpdate = async (params) => {
    const {
      data: rowData,
      colDef: { field },
      newValue,
      oldValue,
    } = params;
    if (newValue === oldValue) return;

    // Per-cell queue: ensure edits to the same {row, field} run sequentially
    if (!handleCellUpdate._queues) handleCellUpdate._queues = new Map();
    const queueKey = `${rowData?.id ?? 'na'}:${field}`;

    const run = async () => {
      // Silent: No status spam — keep UI instant
      try {
        let rpcEndpoint;
        let updateData = { p_id: rowData.id, p_sale_id: selectedSaleId, p_client_id: clientId };
        let finalValue = newValue;
        let markFlags = {};

        switch (field) {
          case 'category':
            rpcEndpoint = 'update_inventory_item_category';
            finalValue = String(finalValue ?? '').trim();
            if (!finalValue) {
              throw new Error('Category is required.');
            }
            updateData.p_category = finalValue;
            break;
          case 'title':
            rpcEndpoint = 'update_inventory_item_title';
            finalValue = titleCase(finalValue);
            updateData.p_title = finalValue;
            markFlags.isTitleEdited = true;
            break;
          case 'description':
            rpcEndpoint = 'update_inventory_item_description';
            updateData.p_description = finalValue;
            break;
          case 'story_points':
            rpcEndpoint = 'update_inventory_item_story_points';
            updateData.p_story_points = finalValue ? finalValue.split(', ').filter(Boolean) : [];
            break;
          case 'resale_positioning':
            rpcEndpoint = 'update_inventory_item_resale_positioning';
            updateData.p_resale_positioning = finalValue ? finalValue.split(', ').filter(Boolean) : [];
            break;
          case 'pricing_context':
            rpcEndpoint = 'update_inventory_item_pricing_context';
            updateData.p_pricing_context = finalValue ? finalValue.split(', ').filter(Boolean) : [];
            break;
          case 'per_item_cost_estimate': {
            rpcEndpoint = 'update_inventory_item_per_item_cost_estimate';
            const n = toNumberOrNull(finalValue);
            updateData.p_per_item_cost_estimate = n;
            finalValue = n;
            markFlags.isPriceEdited = true;
            break;
          }
          case 'quantity_estimate': {
            rpcEndpoint = 'update_inventory_item_quantity_estimate';
            const q = toIntOr(finalValue, 1);
            updateData.p_quantity_estimate = q;
            finalValue = q;
            markFlags.isQtyEdited = true;
            break;
          }
          default:
            throw new Error('Unsupported field for update');
        }
  

        const response = await fetchWrapper(`${process.env.REACT_APP_API_URL}/supabase/rpc/${rpcEndpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updateData),
        });

        if (response.ok) {
          if (gridRef.current && gridRef.current.api && !gridRef.current.api.isDestroyed()) {
            const api = gridRef.current.api;
            const rowNode = api.getRowNode(rowData.id);
            const current = rowNode?.data || rowData; // use the latest data already in the grid
            const newChange = {
              id: `local-${Date.now()}`,
              inventory_item_id: rowData.id,
              field_name: field,
              old_value: oldValue ?? '',
              new_value: finalValue ?? '',
              changed_at: new Date().toISOString(),
            };
            const recentEdits = Array.isArray(current?.recentEdits)
              ? [...current.recentEdits, newChange]
              : [newChange];
            const updatedRow = { ...current, [field]: finalValue, recentEdits, ...markFlags };
            api.applyTransaction({ update: [updatedRow] });
            if (rowNode) {
              api.refreshCells({ rowNodes: [rowNode], force: true, suppressFlash: true });
            }
          }
        } else {
          const errorData = await response.json();
          console.error('Update error data:', errorData);
          setUiMessage(`Update failed: ${errorData.error?.message || 'Unknown error'}`);
          if (gridRef.current && gridRef.current.api && !gridRef.current.api.isDestroyed()) {
            const api = gridRef.current.api;
            const rowNode = api.getRowNode(rowData.id);
            const current = rowNode?.data || rowData;
            const revertedRow = { ...current, [field]: oldValue };
            api.applyTransaction({ update: [revertedRow] });
            if (rowNode) {
              api.refreshCells({ rowNodes: [rowNode], force: true, suppressFlash: true });
            }
          }
        }
      } catch (error) {
        console.error('Cell update error:', error);
        setUiMessage(`Update error: ${error.message}`);
        if (gridRef.current && gridRef.current.api && !gridRef.current.api.isDestroyed()) {
          const api = gridRef.current.api;
          const rowNode = api.getRowNode(rowData.id);
          const current = rowNode?.data || rowData;
          api.applyTransaction({ update: [{ ...current, [field]: oldValue }] });
        }
      }
    };

    const prev = handleCellUpdate._queues.get(queueKey) || Promise.resolve();
    const next = prev.then(run).finally(() => {
      if (handleCellUpdate._queues.get(queueKey) === next) {
        handleCellUpdate._queues.delete(queueKey);
      }
    });
    handleCellUpdate._queues.set(queueKey, next);
    await next;
  };
  // Helper: Blur all modal inputs to flush edits (for mobile)
 
  const handleViewAll = useCallback(() => {
  setSelectedSectionId('all');

  // Clear the search/quick filter so user sees everything
  if (gridRef.current?.api) {
    gridRef.current.api.setGridOption('quickFilterText', '');
  }
}, [gridRef]);


const handleStatusChange = async (status) => {
  if (!gridRef.current || !gridRef.current.api) {
setUiMessage('Error: Grid not initialized');
    return;
  }

  const selectedRows = gridRef.current.api.getSelectedRows() || [];
  if (!selectedRows.length) {
    setUiMessage('No items selected');
    return;
  }

  
  if (status === 'DELETE') {
    const hasTagged = selectedRows.some((row) => row.tagged);
    if (hasTagged) {
      setUiMessage('Cannot delete item with a printed tag.');
      setSelectedStatus('');
      window.alert('Cannot delete item with a printed tag.');
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to permanently delete this item from your inventory?'
    );
    if (!confirmed) {
      // reset dropdown (if you’re using one) and bail out
      setSelectedStatus('');
      return;
    }
  }

  // Removed local setLoading
  try {
    await Promise.all(
      selectedRows.map(async (row) => {
        // Map DELETE to 'deleted' for DB/trigger; others pass as-is (e.g., 'on hold', 'broken')
        const dbStatus = status === 'DELETE' ? 'deleted' : status.toLowerCase();
        const response = await fetchWrapper(
          `${process.env.REACT_APP_API_URL}/supabase/rpc/update_inventory_item_status`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              p_id: row.id,
              p_sale_id: selectedSaleId,
              p_client_id: clientId,
              p_status: dbStatus,
            }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error?.message || `Failed to update item ${row.id} to ${status}`
          );
        }
      })
    );

    await reload(); // Refresh to hide non-ACTIVE items

    if (gridRef.current && gridRef.current.api && !gridRef.current.api.isDestroyed()) {
      gridRef.current.api.deselectAll();
    }

  setUiMessage(`Set ${selectedRows.length} items to "${status}" successfully`);
    setSelectedStatus('');
    setTimeout(() => setUiMessage(''), 1500);
    // Note: Refunds auto-trigger via backend only for status='deleted'
  } catch (error) {
    console.error('Status change error:', error);
    setUiMessage(
      `Error updating status to "${status}": ${error.message}. Contact support@scucciai.com`
    );
  } finally {
    // Removed local setLoading
  }
};
// Recreate commitAll so the modal has a real function
const commitAllEdits = async () => {
  if (!selectedItem) return;
  const api = gridRef.current?.api;
  const rowNode = api?.getRowNode(selectedItem.id);
  const original = rowNode?.data || selectedItem;
  const newTitle = String(modalTitle ?? '');
  const newPrice = toNumberOrNull(modalPrice);
  const newQty = toIntOr(modalQty, 1);
  const originalTitle = String(original?.title ?? '');
  const originalPrice = toNumberOrNull(original?.per_item_cost_estimate);
  const originalQty = toIntOr(original?.quantity_estimate, 1);
  const titleChanged = newTitle !== originalTitle;
  const priceChanged = newPrice !== originalPrice;
  const qtyChanged = newQty !== originalQty;
  const markFlags = {};

  if (titleChanged) {
    await commitTitle(selectedItem.id, newTitle);
    markFlags.isTitleEdited = true;
  }
  if (priceChanged) {
    await commitPrice(selectedItem.id, newPrice);
    markFlags.isPriceEdited = true;
  }
  if (qtyChanged) {
    await commitQty(selectedItem.id, newQty);
    markFlags.isQtyEdited = true;
  }

  if (api) {
    const rowNode = api.getRowNode(selectedItem.id);
    if (rowNode) {
      const updated = {
        ...rowNode.data,
        title: modalTitle,
        per_item_cost_estimate: newPrice,
        quantity_estimate: newQty,
        ...markFlags
      };
      api.applyTransaction({ update: [updated] });
      rowNode.setData(updated);
      api.refreshCells({ rowNodes: [rowNode] });
    }
  }
};
 
   // HOOK: Inventory modal logic (must be top-level)
                // --------------------------------------------------
                const {
                  gotoIndex: hookGotoIndex,
                  gotoRel: hookGotoRel,
                  commitAll: hookCommitAll,
                } = useInventoryModal({
                  gridApi: gridRef.current?.api,
                  pageRows,
                  selectedItem,
                  setSelectedItem,
                  setSelectedImage,
                  setModalTitle,
                  setModalPrice,
                  setModalQty,
                  toNumberOrNull,
                  toIntOr,
                  commitAll: commitAllEdits
                });
             
const handleKey = async (e) => {
       if (!showModal) return;



  // Allow typing inside inputs
  const t = e.target;
  const isTextField =
    t &&
    (t.tagName === "INPUT" ||
     t.tagName === "TEXTAREA" ||
     t.isContentEditable);

  if (isTextField) {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") return;
  }

  if (e.key === "ArrowLeft") {
    e.preventDefault();
    await hookGotoRel(-1);
    return;
  }

  if (e.key === "ArrowRight") {
    e.preventDefault();
    await hookGotoRel(1);
    return;
  }

  if (e.key === "Escape") {
    e.preventDefault();
    await hookCommitAll();
    setShowModal(false);
    setSelectedImage("");
    setSelectedItem(null);
    return;
  }
};
return (
  <>
    {/* Inventory Modal — Always placed OUTSIDE the main page wrapper */}
    <InventoryItemModal
      showModal={showModal}
      selectedItem={selectedItem}
      selectedImage={selectedImage}
      modalTitle={modalTitle}
      modalPrice={modalPrice}
      modalQty={modalQty}
      pageRows={pageRows}
      gotoIndex={hookGotoIndex}
      gotoRel={hookGotoRel}
      commitAll={hookCommitAll}
      setShowModal={setShowModal}
      setSelectedItem={setSelectedItem}
      setSelectedImage={setSelectedImage}
      setModalTitle={setModalTitle}
      setModalPrice={setModalPrice}
      setModalQty={setModalQty}
      handleKey={handleKey}
    />
    {showChangeHistory && (
      <div
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        onClick={closeChangeHistory}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-5xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Change History</h3>
              {changeHistoryItem && (
                <p className="text-xs text-gray-500">
                  {changeHistoryItem.title || changeHistoryItem.filename || 'Item'}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={closeChangeHistory}
              className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div className="p-4">
            <div className="ag-theme-quartz" style={{ height: '50vh', width: '100%' }}>
              <AgGridReact
                rowData={changeHistoryRows}
                columnDefs={changeHistoryColumnDefs}
                domLayout="normal"
                suppressPaginationPanel={true}
                pagination={false}
                headerHeight={36}
                rowHeight={48}
                defaultColDef={{ resizable: true, sortable: true }}
              />
            </div>
          </div>
        </div>
      </div>
    )}

    {/* MAIN SALESINV PAGE */}
    <div
      className="flex flex-col min-h-screen bg-gray-100 pt-16 pb-32"
      style={{
        '--scucci-blue-500': '#2563EB',
        '--scucci-blue-600': '#1D4ED8',
        colorScheme: 'light'
      }}
    >
      <div className="flex-grow flex flex-col items-center justify-start pb-8">
        <div className="w-full mx-auto">
          <h1 className="text-4xl font-bold text-center mb-4">Scucci Estate Sales Inventory</h1>

          <SectionSelector
            sales={sales}
            selectedSaleId={selectedSaleId}
            setSelectedSaleId={setSelectedSaleId}
            sections={sections}
            selectedSectionId={selectedSectionId}
            setSelectedSectionId={setSelectedSectionId}
            handleViewAll={handleViewAll}
            formatSaleDate={formatSaleDate}
          />

          {/* EMPTY SALE MESSAGE */}
          {!dataLoading && selectedSaleId && inventory.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">Please Upload Images to the Sale</p>
            </div>
          )}

          {/* INVENTORY GRID + TOOLBAR + ACTIONS */}
          {!dataLoading && inventory.length > 0 && (
            <div className="overflow-x-auto pb-2">
              <h2 className="text-2xl font-semibold text-center mb-4">Inventory Details</h2>

              <GridToolBar
                isQuickEdit={isQuickEdit}
                toggleQuickEdit={toggleQuickEdit}
                handleSearchChange={handleSearchChange}
                showRestoreGrid={!isQuickEdit && hasColumnChanges}
                handleRestoreGrid={resetColumns}
                hasActiveSort={(sortModel || []).length > 0}
                handleResetSort={clearSortModel}
                tagPreset={tagPreset}
                setTagPreset={setTagPreset}
                labelsToPrint={labelsToPrint}
                handleMakeLabels={handleMakeLabels}
                printOrderPromptOpen={printOrderPrompt.isOpen}
                showPartialSheetLink={isSheetPreset(tagPreset) && !printOrderPrompt.isOpen}
                onOpenPartialSheet={handleOpenPartialSheet}
                startAtSheetOpen={startAtSheetOpen}
                startAtValue={startAtValue}
                startAtMax={getSheetMax(tagPreset)}
                startAtError={startAtError}
                onStartAtChange={(value) => {
                  setStartAtValue(value);
                  if (startAtError) setStartAtError('');
                }}
                onStartAtCancel={handleStartAtCancel}
                onStartAtGenerate={handleStartAtGenerate}
                displayedAskingTotalLabel={displayedAskingTotalLabel}
                askingTotalScopeLabel={selectedSectionName}
              />
              {printOrderPrompt.isOpen && (
                <div className="mx-4 mt-2 flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-800 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    Print order: You’re currently sorting by {printOrderPrompt.sortLabel}. Printing in this order may be harder to tag room-by-room.
                  </div>
                  <div className="flex flex-row gap-2 sm:flex-row sm:items-center w-full sm:w-auto">
                    <button
                      type="button"
                      onClick={() => handlePrintOrderChoice('walk')}
                      className="inline-flex items-center justify-center h-9 px-2.5 rounded-md text-xs sm:text-sm font-medium bg-emerald-600 text-white border border-emerald-600 hover:bg-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-300 w-1/2 sm:w-auto"
                    >
                      Print in Default Order
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePrintOrderChoice('table')}
                      className="inline-flex items-center justify-center h-9 px-2.5 rounded-md text-xs sm:text-sm font-medium bg-white text-amber-900 border border-amber-300 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300 w-1/2 sm:w-auto"
                    >
                      Print in Current Table Order
                    </button>
                  </div>
                </div>
              )}

              {/* AG GRID */}
              <div
                className={`ag-theme-quartz mt-2 overflow-x-auto ${isQuickEdit ? 'phone-mode' : ''}`}
                style={isQuickEdit
                  ? { height: '68vh', width: '100%' }
                  : { height: 'auto', width: '100%' }}
              >
                <AgGridReact
                  className="ag-theme-quartz ag-left-aligned-pagination"
                  pagination={true}
                  paginationPageSize={50}
                  paginationPosition="top"
                  suppressPaginationPanel={false}
                  paginationNumberFormatter={(p) => `${p.value.toLocaleString()}`}
                  domLayout="autoHeight"
                  ref={gridRef}
                  rowData={inventory}
                  columnDefs={columnDefs}
                  context={context}
                  getQuickFilterText={(params) => params?.data?.title ?? ''}
                  suppressAnimationFrame={true}
                  onCellValueChanged={handleCellUpdate}
                  stopEditingWhenCellsLoseFocus={false}
                  singleClickEdit={false}
                  suppressDragLeaveHidesColumns={isQuickEdit}
                  suppressMovableColumns={isQuickEdit}
                  suppressHorizontalScroll={false}
                  defaultColDef={{
                    resizable: true,
                    cellEditorParams: (params) => ({
                      ...params.cellEditorParams,
                      selectAllOnFocus: false
                    })
                  }}
                  rowSelection={{ mode: 'multiRow' }}
                  selectionColumnDef={{ width: 38 }}
                  onGridReady={handleGridReadyWithPager}
                  onFirstDataRendered={handleFirstDataRenderedWithPager}
                  getRowId={(params) => params.data.id}
                  onSortChanged={handleSortChangedWithPager}
                  onPaginationChanged={refreshPaginationInfo}
                  onFilterChanged={refreshPaginationInfo}
                  onRowDataUpdated={refreshPaginationInfo}
                  onSelectionChanged={handleSelectionChanged}
                  onRowClicked={handleRowClicked}
                  onColumnMoved={handleColumnStateChange}
                  onColumnVisible={handleColumnStateChange}
                  onColumnPinned={handleColumnStateChange}
                  onColumnResized={handleColumnStateChange}
                  headerHeight={38}
                  suppressMenuHide={true}
                  rowHeight={60}
                  rowClassRules={{
                    'ag-row-even': 'rowIndex % 2 === 0',
                    'ag-row-odd': 'rowIndex % 2 === 1',
                  }}
                  getRowStyle={(params) => {
                    if (params.data.tagged) return { backgroundColor: 'rgba(34,197,94,0.1)' };
                    return null;
                  }}
                  deltaRowDataMode={true}
                  animateRows={false}
                />
              </div>

              <InventoryBottomPager
                currentPage={paginationInfo.currentPage}
                totalPages={paginationInfo.totalPages}
                onFirst={() => gridRef.current?.api?.paginationGoToFirstPage?.()}
                onPrev={() => gridRef.current?.api?.paginationGoToPreviousPage?.()}
                onNext={() => gridRef.current?.api?.paginationGoToNextPage?.()}
                onLast={() => gridRef.current?.api?.paginationGoToLastPage?.()}
              />

              <InventoryActionsBar
                handleExportCsv={handleExportCsv}
                setShowRestoreModal={setShowRestoreModal}
                handleOpenManualTags={handleOpenManualTags}
                handleOpenHelp={() => setShowHelpModal(true)}
                selectedCount={selectedCount}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                handleStatusChange={handleStatusChange}
                handleOpenMoveModal={handleOpenMoveModal}
                moveLoading={moveLoading}
                handleUndoLastEdit={handleUndoLastEdit}
                selectedSaleId={selectedSaleId}
              />

              <InventoryHelpModal
                isOpen={showHelpModal}
                onClose={() => setShowHelpModal(false)}
              />

              {/* UI MESSAGES & FEEDBACK */}
              {dataLoading && (
                <p className="text-center text-sm text-gray-700 mb-4">Loading inventory...</p>
              )}
              {!dataLoading && sales.length === 0 && (
                <p className="text-center text-sm text-gray-700 mb-4">No active sales found</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
     <RestoreItemsModal
      isOpen={showRestoreModal}
      onClose={() => setShowRestoreModal(false)}
      selectedSaleId={selectedSaleId}
      clientId={clientId}
      onRestoreSuccess={reload}
      formatUSD={formatUSD}
      toIntOr={toIntOr}
       setUiMessage={setUiMessage}
    />
    {/* MOVE ITEMS MODAL — keep outside main wrapper */}
    <MoveItemsModal
  isOpen={showMoveModal}   // ← change from show= to isOpen=
  onClose={handleCloseMoveModal}
  moveSections={moveSections}
  moveSelectedSectionId={moveSelectedSectionId}
  setMoveSelectedSectionId={setMoveSelectedSectionId}
  selectedSaleId={selectedSaleId}
  clientId={clientId}
  fetchWrapper={fetchWrapper}
  onSuccess={reload}
  selectedItemIds={gridRef.current?.api?.getSelectedRows().map(r => r.id) || []}
  moveError={moveError}
/>
    <ManualTagsModal
      isOpen={showManualTagsModal}
      onClose={() => setShowManualTagsModal(false)}
      onSubmit={handleManualTagsSubmit}
      isSubmitting={manualTagsLoading}
      tagPreset={tagPreset}
      setTagPreset={setTagPreset}
    />
    <FloatingToast message={uiMessage} onDismiss={() => setUiMessage('')} />
  </>
);
}
export default SalesInv;
